import { useEffect, useState } from "react";
import { environment } from "../../environment";

export interface Doc2CompanyAgentConfig {
  agentId: string | undefined;
  serviceAccountId: string | undefined;
  isConfigured: boolean;
  isLoading: boolean;
}

/**
 * The Doc2Company agent id and its execution service-account id, both static
 * environment values (`DOC2COMPANY_AGENT_ID` /
 * `DOC2COMPANY_SERVICE_ACCOUNT_ID`) — same pattern as
 * {@link useCompanyProvisionAgentId}.
 *
 * `isConfigured` is true only once BOTH are present, since an invocation needs
 * both. Until a deployment supplies them the document upload stays hidden and
 * Bulk create company remains CSV-only, which is why the Octopus variables
 * default to empty rather than to a placeholder UUID.
 */
export function useDoc2CompanyAgentId(): Doc2CompanyAgentConfig {
  const [agentId, setAgentId] = useState<string | undefined>();
  const [serviceAccountId, setServiceAccountId] = useState<
    string | undefined
  >();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    environment()
      .then((env) => {
        if (cancelled) return;
        setAgentId(env.DOC2COMPANY_AGENT_ID.trim() || undefined);
        setServiceAccountId(
          env.DOC2COMPANY_SERVICE_ACCOUNT_ID.trim() || undefined,
        );
      })
      .catch(() => {
        if (!cancelled) {
          setAgentId(undefined);
          setServiceAccountId(undefined);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    agentId,
    serviceAccountId,
    isConfigured: !!agentId && !!serviceAccountId,
    isLoading,
  };
}
